/**
 * T558 — the two React adapters over `~/lib/nav-pending`, one per control shape.
 *
 * A member control that navigates has a destination or it does not, and the two cases need
 * different rules. `useNavPendingTo` covers the first: compare where React Router is going
 * against where the control points. `useNavPendingSelf` covers the second — `navigate(-1)`,
 * where a history step is a position and not a path, so there is nothing to compare and
 * ownership has to be established another way.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigation } from "react-router";
import { isNavigationTargeting } from "~/lib/nav-pending";

/**
 * How long after its own click a control may still claim the navigation it sees.
 *
 * This is the guard half of R-T558-2, and the reason it is a decaying stamp rather than a
 * boolean: a bare "I started something" flag outlives the click that set it. If the click's
 * `navigate()` turns out to be a no-op nothing re-renders, so nothing clears the flag, and
 * the *next* navigation — started by a tab, a card, anything — inherits it and lights this
 * control. That is the failure R-T556-1 forbids on the tab bar, arriving through the door
 * the `navigate(-1)` shape leaves open. A stamp decays instead: after one second it can no
 * longer claim anything, so the worst case of a no-op click is a control that stays silent.
 *
 * One second is far longer than the gap between a click and React Router entering
 * `loading` (same tick), and far shorter than the gap to any navigation she starts by hand
 * afterwards.
 */
const SELF_NAV_WINDOW_MS = 1000;


/**
 * Does this click actually start an in-app navigation?
 *
 * A cmd/ctrl/shift-click opens a new tab and a middle-click opens a background one: the
 * current document never navigates, so React Router never enters `loading` and nothing
 * clears the stamp. It would sit live for the whole window, and the NEXT navigation —
 * started by anything — could inherit it. That is exactly the R-T558-2 failure the decaying
 * stamp exists to bound, so the cheapest fix is not to write the stamp at all.
 *
 * `defaultPrevented` is included because a caller that cancels the click has cancelled the
 * navigation too.
 *
 * `target` is the same category by a different door, and it is the one this guard missed
 * first time round: React Router's `<Link>` also declines an in-app transition when the
 * anchor carries a `target` other than `_self`, so the document stays put and the stamp
 * would again outlive the click. No call site passes `target` today — but `NavPendingLink`
 * spreads `...rest` onto the anchor, 16 `target="_blank"` links already live in the member
 * area, and T558's own ledger excludes controls for precisely this reason. A guard that
 * enumerates modifier keys and stays silent about the other way out is not exhaustive; it
 * is lucky. Both clauses were raised by the AI review on #1389 (rounds 1 and 2).
 */
export function startsInAppNavigation(e: {
  defaultPrevented: boolean;
  metaKey: boolean;
  ctrlKey: boolean;
  shiftKey: boolean;
  altKey: boolean;
  button: number;
  currentTarget?: { target?: string } | null;
}): boolean {
  const anchorTarget = e.currentTarget?.target;
  return (
    !e.defaultPrevented &&
    !e.metaKey &&
    !e.ctrlKey &&
    !e.shiftKey &&
    !e.altKey &&
    e.button === 0 &&
    (!anchorTarget || anchorTarget === "_self")
  );
}

/** A relative href resolved the way the browser resolves it against the current URL. */
function resolveRelative(to: string, location: { pathname: string; search?: string }): string | null {
  try {
    const url = new URL(to, `http://local${location.pathname}${location.search ?? ""}`);
    return `${url.pathname}${url.search}`;
  } catch {
    return null;
  }
}

/**
 * R-T558-3, as narrowed by T560 — a control lights for the navigation **its own tap
 * started**, and only while that navigation is going where the control points.
 *
 * The original rule lit by destination alone, and that premise was false: a destination
 * does not identify a control. On the phase screen the member header's `My Space` link and
 * the `Back to My Space` control resolve the SAME path, so one tap lit both — the two-lit-
 * controls failure R-T556-3 forbids, arriving through a door T558 did not consider. T558
 * closed the hole where two mappers disagree about a path; this closes the hole where two
 * controls legitimately share one.
 *
 * Ownership is the trigger, the destination is a guard. Both are needed:
 *  - without ownership, every control pointing at the destination lights (the defect);
 *  - without the destination, a control stays lit after she taps it again toward somewhere
 *    else, because the stamp says "I started something" and cannot say what.
 *
 * Call `onNavigate` from the control's own click handler. A control that never calls it
 * never lights — deliberately: silence is the safe failure, a control lit for someone
 * else's navigation is the bug this exists to prevent.
 *
 * NOTE the tab bar does NOT use this hook and must not: R-T556-1 makes it destination-
 * based on purpose, so it marks where she is going no matter who started the trip.
 */
export function useNavPendingTo(to: string | null | undefined): {
  pending: boolean;
  onNavigate: () => void;
} {
  const navigation = useNavigation();
  const location = useLocation();
  const { pending: startedHere, markNavigationStart } = useNavPendingSelf();
  // T972 — a relative `to` (`?page=2`, `../x`) names a place too; resolve it against the
  // route before comparing, or the control can never claim its own navigation.
  // T972 — a relative `to` names a place too. Resolved with URL semantics against the
  // current location: every relative href in the app is a query (`?page=2`) or a hash, where
  // URL and route-relative resolution agree. (`useResolvedPath` would agree for `../x` too,
  // but it couples every link to a full router context; none of those hrefs exist.)
  const target = to == null ? null : to.startsWith("/") ? to : resolveRelative(to, location);

  const targetsThisControl =
    target != null &&
    isNavigationTargeting(
      navigation.state !== "idle" ? navigation.location : null,
      target,
      location,
    );

  // T972 — once her tap's navigation was seen heading here, a loader redirect (a CTA
  // bounced to onboarding) changes `navigation.location` mid-flight. The claim follows a
  // redirect (React Router marks its location `state._isRedirect`) and is dropped by any
  // other navigation that supersedes hers: a control never stays lit for someone else's.
  const claimed = useRef(false);
  const isRedirect = (navigation.location?.state as { _isRedirect?: boolean } | null)?._isRedirect === true;
  if (!startedHere || navigation.state === "idle") claimed.current = false;
  else if (targetsThisControl) claimed.current = true;
  else if (!isRedirect) claimed.current = false;

  return { pending: startedHere && (targetsThisControl || claimed.current), onNavigate: markNavigationStart };
}

/**
 * R-T558-2 — the control does not know where it is going, so it lights only for the
 * navigation its own click started.
 *
 * Call `markNavigationStart()` in the click handler, immediately before `navigate(...)`.
 *
 * The claim latches into state, so the window gates when a navigation *starts* and never
 * how long it runs: a hop slower than `SELF_NAV_WINDOW_MS` keeps its pending state for its
 * whole duration, and only `idle` clears it.
 */
export function useNavPendingSelf(): {
  pending: boolean;
  markNavigationStart: () => void;
} {
  const navigation = useNavigation();
  const startedAt = useRef(0);
  const [pending, setPending] = useState(false);

  /*
   * The dep list is `[navigation.state]` on purpose, and the gap it leaves is deliberate:
   * tapping this control while a navigation is ALREADY `loading` writes the stamp but
   * fires no effect, so the control stays dark for that hop. It fails safe — silence, not
   * a control lit for someone else's navigation — and widening the deps to re-run on every
   * `navigation` object would hand the stamp exactly the in-flight navigation R-T558-2
   * exists to keep it away from. Leave it as it is.
   */
  useEffect(() => {
    if (navigation.state === "idle") {
      setPending(false);
      return;
    }
    if (Date.now() - startedAt.current < SELF_NAV_WINDOW_MS) setPending(true);
  }, [navigation.state]);

  // A ref and not state: recording the click must not itself cause a render, or the
  // recording would race the navigation it is trying to time.
  const markNavigationStart = useCallback(() => {
    startedAt.current = Date.now();
  }, []);

  return { pending, markNavigationStart };
}
